import * as React from 'react';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { ICustomer } from '../../../data/entities/customer';
import { IFinancial } from '../../../data/entities/customer/financial';
import { actions } from '../../../data/store';
import { ISessionState } from '../../../entities/auth/session/reducers';
import { currentUserId } from '../../../entities/auth/session/selectors';
import { resetPassword, updateCurrentUser } from '../../../entities/auth/user/actions';
import { IThunkDispatch } from '../../../store/middleware';
import { IState } from '../../../store/reducer';
import { SaveState } from '../../../types';
import { useDataLoad } from '../../helpers/hooks';
import { customerForEditActions } from './actions';
import { CustomerComponent } from './Customer';
import { combineSaveState } from './helpers';
import { SupportedPaymentTypes } from './payment/CustomerPaymentDetails';
import { IValidationResult } from './validation';

const hotswapIntlToAccount = (financial: IFinancial) => {
	if (financial.payment_method === SupportedPaymentTypes.NON_IBAN) {
		return { ...financial, account_number: financial.intl_account_number };
	}
	return financial;
};

interface ICustomerContainerProps {
	getCustomer: (id: string) => any;
	getCustomerFinancial: (id: string) => any;
	customerId: string;
	authUser: ISessionState;
	customer: ICustomer | null;
	customerTouched: boolean;
	financial: IFinancial | null;
	financialTouched: boolean;
	saving: boolean;
	saveState: SaveState;
	resettingPassword: { state: SaveState, context: {} | Error };
	validation: IValidationResult[];
	isImpersonating: boolean;
	passwordless: boolean;
	onChange(customer: ICustomer): any;
	onChangeFinancial(payment: IFinancial): any;
	onSubmit(customer: ICustomer): any;
	onSubmitFinancial(financial: IFinancial): any;
	onSubmitAttributes(attrs: { [key: string]: string }): Promise<any>;
	onPasswordReset(customer: ICustomer, from: string, to: string): any;
}

const mapDispatchToProps = (dispatch: IThunkDispatch) => ({
	getCustomer: (id: string) => dispatch(actions.customer.getById(id)),
	getCustomerFinancial: (id: string) => dispatch(actions.financial.getByOwnerId(id)),
	onChange: (customer: ICustomer) => dispatch(customerForEditActions.CUSTOMER_UI_CHANGED({ id: customer.id, customer })),
	onChangeFinancial: (financial: IFinancial) =>
		dispatch(customerForEditActions.CUSTOMER_FINANCIAL_UI_CHANGED({ id: financial.customer, financial })),
	onSubmit: (customer: ICustomer) => dispatch(actions.customer.update(customer)),
	onSubmitFinancial: (financial: IFinancial) => dispatch(actions.financial.upsert(financial.customer, hotswapIntlToAccount(financial))),
	onSubmitAttributes: (attrs: { [key: string]: string }) => dispatch(updateCurrentUser(attrs)),
	onPasswordReset: (customer: ICustomer, from: string, to: string) => {
		dispatch(resetPassword(customer.id, from, to));
	}
});

const mapStateToProps = () => (state: IState) => {
	const customerId = currentUserId(state);
	const customerDraft = state.views.customer.drafts[customerId];
	const financialDraft = state.views.customer.financialDrafts[customerId];
	const passwordless = (state.entities.auth.session.current.attributes['custom:passwordless'] || '').toLowerCase() === 'y';
	const {
		saving,
		financialSaving,
		saveState,
		financialSaveState,
		resettingPassword,
		validation,
		financialValidation
	} = state.views.customer;

	const authUser = state.entities.auth.session.current;

	const validationResults: IValidationResult[] = (validation[customerId] || []).concat(financialValidation[customerId] || [
		{
			key: 'correspondence_address1',
			tab: 'business',
			result: {
				validated: false,
				valid: false,
				desc: 'You must specify an address'
			}
		},
		{
			key: 'correspondence_city',
			tab: 'business',
			result: {
				validated: false,
				valid: false,
				desc: 'You must specify a city'
			}
		},
		{
			key: 'correspondence_postcode',
			tab: 'business',
			result: {
				validated: false,
				valid: false,
				desc: 'You must specify a postcode'
			}
		},
		{
			key: 'correspondence_country_code',
			tab: 'business',
			result: {
				validated: false,
				valid: false,
				desc: 'You must specify a country'
			}
		},
		{
			key: 'payment_method',
			tab: 'business',
			result: {
				valid: false,
				validated: false,
			}
		}
	]);
	return {
		customer: customerDraft ? customerDraft.customer : null,
		customerTouched: customerDraft ? customerDraft.touched : false,
		financial: financialDraft ? financialDraft.financial : null,
		financialTouched: financialDraft ? financialDraft.touched : false,
		customerId,
		authUser,
		saving: saving || financialSaving,
		saveState: combineSaveState(saveState, financialSaveState),
		resettingPassword,
		validation: validationResults,
		isImpersonating: state.entities.admin.isImpersonating,
		passwordless
	};
};

const getEmptyFinancial = (customerId: string): IFinancial => ({
	customer: customerId,
	account_holder_name: '',
	account_number: '',
	intl_account_number: '',
	sort_code: '',
	swift: '',
	iban: '',
	currency: '',
	paypal_account: '',
	payment_method: '',
	trading_name: '',
	invoice_email: '',
	vat_number: '',
	vat_country: '',
	correspondence_address1: '',
	correspondence_address2: '',
	correspondence_city: '',
	correspondence_postcode: '',
	correspondence_country_code: '',
	business_address1: '',
	business_address2: '',
	business_city: '',
	business_postcode: '',
	business_country_code: ''
});

const CustomerContainer = (props: ICustomerContainerProps) => {
	const location = useLocation();

	const customerLoading = useDataLoad(
		!props.customer,
		() => props.getCustomer(props.customerId)
	);
	const financialLoading = useDataLoad(
		!props.financial,
		() => props.getCustomerFinancial(props.customerId)
	);

	return <CustomerComponent
		{...props}
		route={location.pathname}
		financial={props.financial || getEmptyFinancial(props.customerId)}
		loading={customerLoading || financialLoading}
	/>;
};

export const Customer = connect(mapStateToProps, mapDispatchToProps)(CustomerContainer);
